/**
 * React Query hooks for User Management
 * Provides hooks for fetching users, updating roles, etc.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  fetchUsers,
  fetchUser,
  updateUserRole,
  fetchRoles,
  adminKeys,
} from '@/api/admin'
import type { ListUsersRequest, RoleCode } from '@/types/admin'

/**
 * Hook for fetching paginated user list
 */
export function useUserList(params: ListUsersRequest) {
  return useQuery({
    queryKey: adminKeys.userList(params),
    queryFn: () => fetchUsers(params),
  })
}

/**
 * Hook for fetching a single user by OID
 */
export function useUserDetail(oid: string) {
  return useQuery({
    queryKey: adminKeys.userDetail(oid),
    queryFn: () => fetchUser(oid),
    enabled: !!oid,
  })
}

/**
 * Mutation hook for updating a user's role
 */
export function useUpdateUserRole() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      oid,
      role,
      reason,
    }: {
      oid: string
      role: RoleCode
      reason?: string
    }) => updateUserRole(oid, role, reason),
    onSuccess: () => {
      // Invalidate user queries to refetch fresh data
      queryClient.invalidateQueries({ queryKey: adminKeys.users() })
    },
  })
}

/**
 * Hook for fetching all available roles
 * Uses infinite staleTime since roles rarely change
 */
export function useRoles() {
  return useQuery({
    queryKey: adminKeys.roles(),
    queryFn: fetchRoles,
    staleTime: Infinity,
  })
}
